import { supabase } from '@/lib/supabase';
import { deleteFromR2 } from '@/lib/r2-storage';
import { FileItem, FileShareRecord, SharePermission } from '../types';

export class SupabaseFileStorage {
  static async getOrCreateProjectsFolder(
    workspaceId: string,
    userId?: string
  ): Promise<{ id: string; name: string } | null> {
    try {
      // Look for existing Projects root folder
      const { data: existing } = await supabase
        .from('workspace_files')
        .select('id, name')
        .eq('workspace_id', workspaceId)
        .eq('name', 'Projects')
        .eq('type', 'folder')
        .is('parent_id', null)
        .eq('trashed', false)
        .maybeSingle();

      if (existing) {
        return existing;
      }

      if (!userId) {
        const { data: authData } = await supabase.auth.getUser();
        userId = authData?.user?.id;
      }

      if (!userId) {
        return {
          id: `projects-root-${workspaceId}`,
          name: 'Projects',
        };
      }

      // Create Projects default folder
      const { data: created, error } = await supabase
        .from('workspace_files')
        .insert({
          workspace_id: workspaceId,
          name: 'Projects',
          type: 'folder',
          parent_id: null,
          size: 0,
          starred: false,
          shared: false,
          trashed: false,
          uploaded_by: userId,
        })
        .select('id, name')
        .maybeSingle();

      if (error || !created) {
        return {
          id: `projects-root-${workspaceId}`,
          name: 'Projects',
        };
      }

      return created;
    } catch {
      return {
        id: `projects-root-${workspaceId}`,
        name: 'Projects',
      };
    }
  }

  static async getFiles(workspaceId: string): Promise<FileItem[]> {
    try {
      const [{ data, error }, { data: projectsData }] = await Promise.all([
        supabase
          .from('workspace_files')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false }),
        supabase
          .from('projects')
          .select('id, name, folder_id')
          .eq('workspace_id', workspaceId),
      ]);

      if (error) throw error;

      let allFiles = [...(data || [])];

      // Find or create "Projects" root folder
      let projectsFolder = allFiles.find(
        (f: any) =>
          f.type === 'folder' &&
          !f.parent_id &&
          f.name?.toLowerCase() === 'projects' &&
          !f.trashed
      );

      let projectsFolderId = projectsFolder?.id;

      if (!projectsFolder) {
        const createdFolder = await this.getOrCreateProjectsFolder(workspaceId);
        if (createdFolder) {
          projectsFolderId = createdFolder.id;
          projectsFolder = {
            id: createdFolder.id,
            name: 'Projects',
            type: 'folder',
            parent_id: null,
            size: 0,
            starred: false,
            shared: false,
            trashed: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          allFiles = [projectsFolder, ...allFiles];
        }
      }

      const projectFolderMap = new Map<string, string>(); // folder_id -> project_id
      const projectNameMap = new Map<string, string>(); // project_name -> project_id
      (projectsData || []).forEach((p: any) => {
        if (p.folder_id) projectFolderMap.set(p.folder_id, p.id);
        if (p.name) projectNameMap.set(p.name.toLowerCase().trim(), p.id);
      });

      return allFiles.map((file: any) => {
        let projectId = file.project_id || undefined;
        let isProtected = false;
        let isDefault = false;

        if (projectsFolderId && (file.id === projectsFolderId || file.id === `projects-root-${workspaceId}`)) {
          isProtected = true;
          isDefault = true;
        }

        // Identify project folders
        if (!projectId && file.type === 'folder' && file.id !== projectsFolderId && file.id !== `projects-root-${workspaceId}`) {
          projectId = projectFolderMap.get(file.id) || projectNameMap.get(file.name?.toLowerCase()?.trim());
        }

        let parentId = file.parent_id || undefined;

        // If it's a project root folder and not inside Projects folder, nest it under Projects folder
        if (projectId && projectsFolderId && (!parentId || parentId === projectsFolderId || parentId === `projects-root-${workspaceId}`)) {
          parentId = projectsFolderId;
          if (file.parent_id !== projectsFolderId && !projectsFolderId.startsWith('projects-root-')) {
            // Auto backfill in DB
            supabase
              .from('workspace_files')
              .update({ project_id: projectId, parent_id: projectsFolderId })
              .eq('id', file.id)
              .then(() => {}, console.error);
          }
        } else if (projectId && !file.project_id) {
          // Auto backfill project_id
          supabase
            .from('workspace_files')
            .update({ project_id: projectId })
            .eq('id', file.id)
            .then(() => {}, console.error);
        }

        return {
          id: file.id,
          name: file.name,
          type: file.type as 'file' | 'folder',
          size: file.size,
          mimeType: file.mime_type,
          parentId,
          projectId,
          isProtected,
          isDefault,
          createdAt: new Date(file.created_at),
          modifiedAt: new Date(file.updated_at),
          starred: file.starred || false,
          shared: file.shared || false,
          url: file.url,
          trashed: file.trashed || false,
          trashedAt: file.trashed_at ? new Date(file.trashed_at) : undefined,
          originalParentId: file.original_parent_id || undefined,
          ownerId: file.uploaded_by,
          ownerName: file.owner_name,
          ownerAvatarUrl: file.owner_avatar_url,
          modifiedBy: file.modified_by,
        };
      });

      // Deduplicate project folders under projectsFolderId
      if (projectsFolderId) {
        const seenProjectKeys = new Map<string, FileItem>();
        const duplicateFolderIdsToRemove = new Set<string>();

        // Sort so that folders linked to projects (or earliest created) come first
        const projectFolders = mappedFiles.filter(
          f => f.type === 'folder' && f.parentId === projectsFolderId && !f.trashed
        );

        for (const folder of projectFolders) {
          const key = (folder.projectId || folder.name).toLowerCase().trim();
          if (!seenProjectKeys.has(key)) {
            seenProjectKeys.set(key, folder);
          } else {
            const canonical = seenProjectKeys.get(key)!;
            duplicateFolderIdsToRemove.add(folder.id);

            // Re-point any children of the duplicate folder to the canonical folder in DB
            supabase
              .from('workspace_files')
              .update({ parent_id: canonical.id })
              .eq('parent_id', folder.id)
              .then(() => {}, console.error);

            // Trash the duplicate folder row in DB
            supabase
              .from('workspace_files')
              .update({ trashed: true })
              .eq('id', folder.id)
              .then(() => {}, console.error);
          }
        }

        if (duplicateFolderIdsToRemove.size > 0) {
          // Re-point in-memory child files
          mappedFiles.forEach(f => {
            if (f.parentId && duplicateFolderIdsToRemove.has(f.parentId)) {
              const dupFolder = projectFolders.find(pf => pf.id === f.parentId);
              if (dupFolder) {
                const key = (dupFolder.projectId || dupFolder.name).toLowerCase().trim();
                const canonical = seenProjectKeys.get(key);
                if (canonical) {
                  f.parentId = canonical.id;
                }
              }
            }
          });

          // Filter out the duplicate folders
          return mappedFiles.filter(f => !duplicateFolderIdsToRemove.has(f.id));
        }
      }

      return mappedFiles;
    } catch (error) {
      console.error('Error fetching files:', error);
      return [];
    }
  }

  static async addFile(
    workspaceId: string,
    userId: string,
    file: Omit<FileItem, 'id' | 'createdAt' | 'modifiedAt'>
  ): Promise<FileItem | null> {
    try {
      const { data, error } = await supabase
        .from('workspace_files')
        .insert({
          workspace_id: workspaceId,
          name: file.name,
          type: file.type,
          size: file.size || 0,
          mime_type: file.mimeType,
          parent_id: file.parentId,
          url: file.url,
          starred: file.starred,
          shared: file.shared,
          trashed: false,
          uploaded_by: userId,
          owner_name: file.ownerName,
          owner_avatar_url: file.ownerAvatarUrl,
          modified_by: file.modifiedBy,
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        name: data.name,
        type: data.type as 'file' | 'folder',
        size: data.size,
        mimeType: data.mime_type,
        parentId: data.parent_id || undefined,
        createdAt: new Date(data.created_at),
        modifiedAt: new Date(data.updated_at),
        starred: data.starred || false,
        shared: data.shared || false,
        url: data.url,
        trashed: false,
        ownerId: data.uploaded_by,
        ownerName: data.owner_name,
        ownerAvatarUrl: data.owner_avatar_url,
        modifiedBy: data.modified_by,
      };
    } catch (error) {
      console.error('Error adding file:', error);
      return null;
    }
  }

  static async updateFile(
    fileId: string,
    updates: Partial<{
      name: string;
      starred: boolean;
      shared: boolean;
      trashed: boolean;
      trashedAt: Date;
      originalParentId: string;
      parentId: string;
      modifiedBy: string;
    }>
  ): Promise<boolean> {
    try {
      // Prevent renaming or moving default Projects root folder
      if (updates.name !== undefined || updates.parentId !== undefined || updates.trashed !== undefined) {
        const { data: target } = await supabase
          .from('workspace_files')
          .select('name, parent_id')
          .eq('id', fileId)
          .maybeSingle();

        if (target?.name === 'Projects' && !target?.parent_id) {
          if (updates.name && updates.name !== 'Projects') return false;
          if (updates.trashed) return false;
          if (updates.parentId) return false;
        }
      }

      const dbUpdates: any = {};

      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.starred !== undefined) dbUpdates.starred = updates.starred;
      if (updates.shared !== undefined) dbUpdates.shared = updates.shared;
      if (updates.trashed !== undefined) dbUpdates.trashed = updates.trashed;
      if (updates.trashedAt !== undefined) dbUpdates.trashed_at = updates.trashedAt.toISOString();
      if (updates.originalParentId !== undefined) dbUpdates.original_parent_id = updates.originalParentId;
      if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId;
      if (updates.modifiedBy !== undefined) dbUpdates.modified_by = updates.modifiedBy;

      const { error } = await supabase
        .from('workspace_files')
        .update(dbUpdates)
        .eq('id', fileId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating file:', error);
      return false;
    }
  }

  static async deleteFile(fileId: string): Promise<boolean> {
    try {
      // Prevent deleting default Projects folder
      const { data: target } = await supabase
        .from('workspace_files')
        .select('name, parent_id')
        .eq('id', fileId)
        .maybeSingle();

      if (target?.name === 'Projects' && !target?.parent_id) {
        console.warn('Cannot delete default Projects folder');
        return false;
      }

      // Move to trash instead of permanent deletion
      const { error } = await supabase
        .from('workspace_files')
        .update({
          trashed: true,
          trashed_at: new Date().toISOString(),
        })
        .eq('id', fileId);

      if (error) throw error;

      // Also trash all children if it's a folder
      const { error: childrenError } = await supabase
        .from('workspace_files')
        .update({
          trashed: true,
          trashed_at: new Date().toISOString(),
        })
        .eq('parent_id', fileId);

      if (childrenError) throw childrenError;

      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  }

  static async restoreFile(fileId: string): Promise<boolean> {
    try {
      // Get the file to find its original parent
      const { data: file, error: fetchError } = await supabase
        .from('workspace_files')
        .select('original_parent_id')
        .eq('id', fileId)
        .single();

      if (fetchError) throw fetchError;

      // Restore file
      const { error } = await supabase
        .from('workspace_files')
        .update({
          trashed: false,
          trashed_at: null,
          parent_id: file.original_parent_id,
          original_parent_id: null,
        })
        .eq('id', fileId);

      if (error) throw error;

      // Also restore children
      const { error: childrenError } = await supabase
        .from('workspace_files')
        .update({
          trashed: false,
          trashed_at: null,
        })
        .eq('parent_id', fileId);

      if (childrenError) throw childrenError;

      return true;
    } catch (error) {
      console.error('Error restoring file:', error);
      return false;
    }
  }

  static async permanentlyDeleteFile(fileId: string): Promise<boolean> {
    try {
      // Prevent deleting default Projects folder
      const { data: target } = await supabase
        .from('workspace_files')
        .select('name, parent_id')
        .eq('id', fileId)
        .maybeSingle();

      if (target?.name === 'Projects' && !target?.parent_id) {
        console.warn('Cannot permanently delete default Projects folder');
        return false;
      }

      // Find files connected to S3/R2 to delete their objects
      const { data: filesToDelete, error: fetchError } = await supabase
        .from('workspace_files')
        .select('id, url')
        .or(`id.eq.${fileId},parent_id.eq.${fileId}`);

      if (!fetchError && filesToDelete) {
        for (const file of filesToDelete) {
          if (file.url && file.url.includes('agencyplus.io')) {
            await deleteFromR2(file.url);
          }
        }
      }

      // Delete children first
      await supabase
        .from('workspace_files')
        .delete()
        .eq('parent_id', fileId);

      // Delete the file itself
      const { error } = await supabase
        .from('workspace_files')
        .delete()
        .eq('id', fileId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error permanently deleting file:', error);
      return false;
    }
  }

  static async getStorageInfo(workspaceId: string): Promise<{ used: number; total: number }> {
    try {
      const { data, error } = await supabase
        .from('workspace_files')
        .select('size')
        .eq('workspace_id', workspaceId)
        .eq('type', 'file')
        .eq('trashed', false);

      if (error) throw error;

      const usedBytes = (data || []).reduce((total: number, file: any) => total + (file.size || 0), 0);
      const totalBytes = 100 * 1024 * 1024; // 100 MB total per workspace

      return {
        used: usedBytes,
        total: totalBytes,
      };
    } catch (error) {
      console.error('Error getting storage info:', error);
      return { used: 0, total: 100 * 1024 * 1024 };
    }
  }

  static async createFolder(
    workspaceId: string,
    userId: string,
    name: string,
    parentId?: string,
    ownerName?: string,
    ownerAvatarUrl?: string | null
  ): Promise<FileItem | null> {
    return this.addFile(workspaceId, userId, {
      name,
      type: 'folder',
      parentId,
      starred: false,
      shared: false,
      trashed: false,
      ownerName,
      ownerAvatarUrl: ownerAvatarUrl || undefined,
      modifiedBy: ownerName,
    });
  }

  // ─── Sharing ────────────────────────────────────────────────────────────────

  /**
   * Returns all file share records for a given file, joined with profile data.
   */
  static async getFileShares(fileId: string): Promise<FileShareRecord[]> {
    try {
      const { data, error } = await supabase
        .from('file_shares')
        .select('*')
        .eq('file_id', fileId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Batch-fetch profiles
      const userIds = data.map((r: any) => r.shared_with_user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url')
        .in('id', userIds);

      // Batch-fetch roles from workspace_members
      const { data: members } = await supabase
        .from('workspace_members')
        .select('user_id, role')
        .in('user_id', userIds);

      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
      const memberMap = new Map((members || []).map((m: any) => [m.user_id, m]));

      return data.map((r: any) => {
        const profile = profileMap.get(r.shared_with_user_id);
        const member = memberMap.get(r.shared_with_user_id);
        const firstName = profile?.first_name || '';
        const lastName = profile?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim() || profile?.email?.split('@')[0] || 'Unknown';
        return {
          id: r.id,
          fileId: r.file_id,
          sharedWithUserId: r.shared_with_user_id,
          sharedByUserId: r.shared_by_user_id,
          permission: r.permission as SharePermission,
          createdAt: new Date(r.created_at),
          userName: fullName,
          userEmail: profile?.email || '',
          userAvatar: profile?.avatar_url || '',
          userRole: member?.role || '',
        };
      });
    } catch (error) {
      console.error('Error fetching file shares:', error);
      return [];
    }
  }

  /**
   * Collects all descendant file IDs within a folder (recursive).
   */
  private static async getDescendantFileIds(folderId: string): Promise<string[]> {
    const ids: string[] = [];
    const queue = [folderId];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const { data } = await supabase
        .from('workspace_files')
        .select('id, type')
        .eq('parent_id', currentId)
        .eq('trashed', false);
      for (const child of data || []) {
        ids.push(child.id);
        if (child.type === 'folder') queue.push(child.id);
      }
    }
    return ids;
  }

  /**
   * Shares a file/folder with multiple users at the given permission level.
   * If the target is a folder, all its descendants are shared recursively.
   * Updates the workspace_files.shared flag automatically.
   */
  static async shareFile(
    fileId: string,
    fileType: 'file' | 'folder',
    userIds: string[],
    permission: SharePermission,
    sharedByUserId: string
  ): Promise<boolean> {
    try {
      // Determine all IDs to share (include descendants for folders)
      let allFileIds = [fileId];
      if (fileType === 'folder') {
        const descendants = await this.getDescendantFileIds(fileId);
        allFileIds = [fileId, ...descendants];
      }

      // Build upsert rows for all file × user combinations
      const rows = allFileIds.flatMap(fid =>
        userIds.map(uid => ({
          file_id: fid,
          shared_with_user_id: uid,
          shared_by_user_id: sharedByUserId,
          permission,
        }))
      );

      if (rows.length > 0) {
        const { error } = await supabase
          .from('file_shares')
          .upsert(rows, { onConflict: 'file_id,shared_with_user_id' });
        if (error) throw error;
      }

      // Sync shared flag on all affected files
      if (allFileIds.length > 0) {
        await supabase
          .from('workspace_files')
          .update({ shared: true })
          .in('id', allFileIds);
      }

      return true;
    } catch (error) {
      console.error('Error sharing file:', error);
      return false;
    }
  }

  /**
   * Removes share records for specific users from a file/folder.
   * If no shares remain, sets workspace_files.shared = false.
   */
  static async unshareFile(
    fileId: string,
    fileType: 'file' | 'folder',
    userIds: string[]
  ): Promise<boolean> {
    try {
      let allFileIds = [fileId];
      if (fileType === 'folder') {
        const descendants = await this.getDescendantFileIds(fileId);
        allFileIds = [fileId, ...descendants];
      }

      const { error } = await supabase
        .from('file_shares')
        .delete()
        .in('file_id', allFileIds)
        .in('shared_with_user_id', userIds);

      if (error) throw error;

      // For each affected file, check if any shares remain
      for (const fid of allFileIds) {
        const { count } = await supabase
          .from('file_shares')
          .select('*', { count: 'exact', head: true })
          .eq('file_id', fid);
        if ((count ?? 0) === 0) {
          await supabase
            .from('workspace_files')
            .update({ shared: false })
            .eq('id', fid);
        }
      }

      return true;
    } catch (error) {
      console.error('Error unsharing file:', error);
      return false;
    }
  }

  /**
   * Updates the permission level for a specific user's share on a file.
   */
  static async updateSharePermission(
    fileId: string,
    userId: string,
    permission: SharePermission
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('file_shares')
        .update({ permission })
        .eq('file_id', fileId)
        .eq('shared_with_user_id', userId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating share permission:', error);
      return false;
    }
  }

  /**
   * Returns all files/folders shared with the given user in a workspace.
   * Used for the "Shared with me" sidebar filter.
   */
  static async getFilesSharedWithMe(userId: string, workspaceId: string): Promise<FileItem[]> {
    try {
      // Get all file IDs shared with this user
      const { data: shares, error: sharesError } = await supabase
        .from('file_shares')
        .select('file_id')
        .eq('shared_with_user_id', userId);

      if (sharesError) throw sharesError;
      if (!shares || shares.length === 0) return [];

      const fileIds = shares.map((s: any) => s.file_id);

      const { data, error } = await supabase
        .from('workspace_files')
        .select('*')
        .in('id', fileIds)
        .eq('workspace_id', workspaceId)
        .eq('trashed', false);

      if (error) throw error;

      return (data || []).map((file: any) => ({
        id: file.id,
        name: file.name,
        type: file.type as 'file' | 'folder',
        size: file.size,
        mimeType: file.mime_type,
        parentId: file.parent_id || undefined,
        projectId: file.project_id || undefined,
        createdAt: new Date(file.created_at),
        modifiedAt: new Date(file.updated_at),
        starred: file.starred || false,
        shared: file.shared || false,
        url: file.url,
        trashed: false,
        ownerId: file.uploaded_by,
        ownerName: file.owner_name,
        ownerAvatarUrl: file.owner_avatar_url,
        modifiedBy: file.modified_by,
      }));
    } catch (error) {
      console.error('Error fetching files shared with me:', error);
      return [];
    }
  }
}

